import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, CheckCircle2, Loader2, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { publicAPI } from '@/services/api';

const ContactPage = () => {
  const [config, setConfig] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    publicAPI.getSiteConfig().then((res) => {
      if (res.data) setConfig(res.data);
    }).catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.full_name.trim()) newErrors.full_name = 'Le nom est requis.';
    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis.";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide.';
    }
    if (!formData.subject.trim()) newErrors.subject = 'Le sujet est requis.';
    if (!formData.message.trim()) {
      newErrors.message = 'Le message est requis.';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Le message doit contenir au moins 10 caractères.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await publicAPI.submitContact(formData);
      setSent(true);
      setFormData({ full_name: '', email: '', phone: '', subject: '', message: '' });
      toast({
        title: 'Message envoyé',
        description: 'Nous vous répondrons dans les plus brefs délais.',
        variant: 'success',
      });
    } catch (err) {
      const data = err?.response?.data || {};
      const detail = data.detail || 'Impossible d\'envoyer le message. Veuillez réessayer.';
      toast({ title: 'Erreur', description: detail, variant: 'destructive' });
      if (data && typeof data === 'object') {
        const fieldErrors = {};
        Object.entries(data).forEach(([key, val]) => {
          if (Array.isArray(val)) fieldErrors[key] = val[0];
          else if (typeof val === 'string' && key !== 'detail') fieldErrors[key] = val;
        });
        if (Object.keys(fieldErrors).length > 0) setErrors(fieldErrors);
      }
    } finally {
      setSending(false);
    }
  };

  const socials = config
    ? [
        { url: config.facebook_url, icon: Facebook, label: 'Facebook' },
        { url: config.twitter_url, icon: Twitter, label: 'Twitter' },
        { url: config.instagram_url, icon: Instagram, label: 'Instagram' },
        { url: config.linkedin_url, icon: Linkedin, label: 'LinkedIn' },
      ].filter((s) => s.url)
    : [];

  return (
    <>
      <Helmet>
        <title>Contact — {config?.platform_name || 'LogiConnect'}</title>
      </Helmet>

      <section className="relative py-32 px-4 bg-gradient-to-br from-[#0d5850] to-teal-700 text-white overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            animate={{ y: [0, -20, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-10 right-[20%] w-[350px] h-[350px] bg-white/5 rounded-full filter blur-3xl"
          />
          <motion.div
            animate={{ y: [0, 30, 0] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
            className="absolute bottom-10 left-[10%] w-[400px] h-[400px] bg-amber-500/10 rounded-full filter blur-3xl"
          />
        </div>
        <div className="container mx-auto relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-amber-300 font-semibold text-sm uppercase tracking-wide">Contactez-nous</span>
            <h1 className="text-4xl md:text-5xl font-bold mt-4 mb-4">
              Nous sommes à votre <span className="text-amber-400">écoute</span>
            </h1>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Une question, une suggestion ou un partenariat ? N'hésitez pas à nous écrire, notre équipe vous répondra rapidement.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Infos de contact */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-2 space-y-8"
            >
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Nos coordonnées</h2>
                <p className="text-gray-600">Retrouvez-nous par tous les canaux disponibles.</p>
              </div>

              <div className="space-y-6">
                {config?.contact_address && (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#0d5850]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#0d5850]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Adresse</h3>
                      <p className="text-gray-600 text-sm">{config.contact_address}</p>
                    </div>
                  </div>
                )}
                {config?.contact_phone && (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#0d5850]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-[#0d5850]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Téléphone</h3>
                      <a href={`tel:${config.contact_phone}`} className="text-gray-600 text-sm hover:text-[#0d5850] transition-colors">
                        {config.contact_phone}
                      </a>
                    </div>
                  </div>
                )}
                {config?.contact_email && (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#0d5850]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-[#0d5850]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Email</h3>
                      <a href={`mailto:${config.contact_email}`} className="text-gray-600 text-sm hover:text-[#0d5850] transition-colors">
                        {config.contact_email}
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {socials.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Suivez-nous</h3>
                  <div className="flex gap-3">
                    {socials.map((s) => (
                      <a
                        key={s.label}
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={s.label}
                        className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-[#0d5850] hover:text-white flex items-center justify-center text-gray-600 transition-all"
                      >
                        <s.icon className="w-5 h-5" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>

            {/* Formulaire */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-3"
            >
              <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-8 md:p-10">
                {sent ? (
                  <div className="text-center py-12 space-y-4">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Message envoyé !</h3>
                    <p className="text-gray-600 max-w-md mx-auto">
                      Merci de nous avoir contactés. Notre équipe vous répondra dans les plus brefs délais.
                    </p>
                    <Button
                      onClick={() => setSent(false)}
                      variant="outline"
                      className="mt-4 border-gray-300"
                    >
                      Envoyer un autre message
                    </Button>
                  </div>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Envoyez-nous un message</h2>
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div className="space-y-2">
                          <Label htmlFor="full_name">
                            Nom complet <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="full_name"
                            name="full_name"
                            placeholder="Votre nom"
                            value={formData.full_name}
                            onChange={handleChange}
                            className={`h-12 ${errors.full_name ? 'border-red-500' : ''}`}
                          />
                          {errors.full_name && <p className="text-xs text-red-600">{errors.full_name}</p>}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">
                            Email <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            placeholder="exemple@email.com"
                            value={formData.email}
                            onChange={handleChange}
                            className={`h-12 ${errors.email ? 'border-red-500' : ''}`}
                          />
                          {errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div className="space-y-2">
                          <Label htmlFor="phone">Téléphone</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            placeholder="+224 6XX XX XX XX"
                            value={formData.phone}
                            onChange={handleChange}
                            className="h-12"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="subject">
                            Sujet <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="subject"
                            name="subject"
                            placeholder="Objet de votre message"
                            value={formData.subject}
                            onChange={handleChange}
                            className={`h-12 ${errors.subject ? 'border-red-500' : ''}`}
                          />
                          {errors.subject && <p className="text-xs text-red-600">{errors.subject}</p>}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">
                          Message <span className="text-red-500">*</span>
                        </Label>
                        <textarea
                          id="message"
                          name="message"
                          rows={5}
                          placeholder="Décrivez votre demande..."
                          value={formData.message}
                          onChange={handleChange}
                          className={`w-full rounded-md border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d5850]/30 focus:border-[#0d5850] resize-none ${
                            errors.message ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {errors.message && <p className="text-xs text-red-600">{errors.message}</p>}
                      </div>

                      <Button
                        type="submit"
                        disabled={sending}
                        className="w-full h-12 bg-gradient-to-r from-[#0d5850] to-teal-600 hover:from-[#0a4740] hover:to-teal-700 text-white text-base"
                      >
                        {sending ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Envoi en cours...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5 mr-2" />
                            Envoyer le message
                          </>
                        )}
                      </Button>
                    </form>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
